import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Banknote,
  CheckCircle2,
  Download,
  FileText,
  Plus,
  Send,
  Trash2,
} from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import Nr2jContinueModal from '../../components/nr2j/Nr2jContinueModal.jsx';
import { useNr2jContinueAction } from '../../hooks/useNr2jContinueAction.js';
import apiService from '../../services/api';
import {
  NR2J_CONTINUE_INTENTS,
  preflightInvoiceAction,
  requirementsFromError,
} from '../../utils/nr2jContinuePreflight.js';
import {
  NR2J_CATALOG_BILLING_TYPES,
  NR2J_INVOICE_PAYMENT_METHODS,
  NR2J_VAT_OPTIONS,
  centsToEuros,
  computeQuoteLineTotals,
  contactDisplayName,
  eurosToCents,
  formatCentsAsEuros,
  formatNr2jDate,
  formatNr2jDateOnly,
  nr2jCreditNoteStatusBadge,
  nr2jCreditStateLabel,
  nr2jInvoiceDisplayBadge,
  nr2jPaymentMethodLabel,
  nr2jPaymentStateLabel,
  organizationDisplayName,
  sumQuoteTotals,
  sumQuoteTotalsByBillingType,
  toDateInputValue,
} from '../../utils/nr2jConstants';

function emptyLine() {
  return {
    key: `${Date.now()}-${Math.random()}`,
    catalogItemId: '',
    name: '',
    description: '',
    quantity: 1,
    unitPriceHtCents: 0,
    priceEuros: '',
    vatRateBps: 2000,
    billingType: 'ONE_TIME',
    discountMode: 'none',
    discountPercent: '',
    discountEuros: '',
  };
}

function lineFromApi(line) {
  let discountMode = 'none';
  let discountPercent = '';
  let discountEuros = '';
  if (line.discountPercentBps != null && line.discountPercentBps !== '') {
    discountMode = 'percent';
    discountPercent = String(Number(line.discountPercentBps) / 100);
  } else if (line.discountAmountHtCents != null && line.discountAmountHtCents !== '') {
    discountMode = 'fixed';
    discountEuros = centsToEuros(line.discountAmountHtCents);
  }
  return {
    key: line.id || `${Date.now()}-${Math.random()}`,
    catalogItemId: line.catalogItemId || '',
    name: line.name || '',
    description: line.description || '',
    quantity: line.quantity || 1,
    unitPriceHtCents: line.unitPriceHtCents || 0,
    priceEuros: centsToEuros(line.unitPriceHtCents),
    vatRateBps: line.vatRateBps ?? 2000,
    billingType: line.billingType || 'ONE_TIME',
    discountMode,
    discountPercent,
    discountEuros,
  };
}

function lineToPayload(line, index) {
  const unitPriceHtCents = line.priceEuros !== ''
    ? eurosToCents(line.priceEuros)
    : Number(line.unitPriceHtCents) || 0;
  const payload = {
    catalogItemId: line.catalogItemId || null,
    sortOrder: index,
    name: line.name.trim(),
    description: line.description?.trim() || null,
    quantity: Math.max(1, Number(line.quantity) || 1),
    unitPriceHtCents,
    vatRateBps: Number(line.vatRateBps),
    billingType: line.billingType,
    discountPercentBps: null,
    discountAmountHtCents: null,
  };
  if (line.discountMode === 'percent' && line.discountPercent !== '') {
    payload.discountPercentBps = Math.round(Number(line.discountPercent) * 100);
  } else if (line.discountMode === 'fixed' && line.discountEuros !== '') {
    payload.discountAmountHtCents = eurosToCents(line.discountEuros);
  }
  return payload;
}

export default function Nr2jInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const {
    continueReq,
    openContinue,
    closeContinue,
    runContinueRetry,
    withActionLock,
  } = useNr2jContinueAction();

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [org, setOrg] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [lines, setLines] = useState([]);
  const [contactId, setContactId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [notesClient, setNotesClient] = useState('');
  const [notesInternal, setNotesInternal] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amountEuros: '',
    method: 'VIREMENT',
    reference: '',
    paidAt: '',
  });

  const isDraft = invoice?.status === 'DRAFT';
  const isIssuedOrSent = invoice?.status === 'ISSUED' || invoice?.status === 'SENT';
  const badge = nr2jInvoiceDisplayBadge(invoice);

  const totals = useMemo(
    () => sumQuoteTotals(lines.map((l) => lineToPayload(l, 0))),
    [lines],
  );
  const billingTotals = useMemo(
    () => sumQuoteTotalsByBillingType(lines.map((l) => lineToPayload(l, 0))),
    [lines],
  );

  function applyInvoiceToForm(inv) {
    setLines((inv.lines || []).length ? inv.lines.map(lineFromApi) : []);
    setContactId(inv.contactId || '');
    setDueAt(toDateInputValue(inv.dueAt));
    setNotesClient(inv.notesClient || '');
    setNotesInternal(inv.notesInternal || '');
    setPaymentTerms(inv.paymentTerms || '');
    setPaymentInstructions(inv.paymentInstructions || '');
  }

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [invRes, catalogRes] = await Promise.all([
        apiService.getNr2jInvoice(id),
        apiService.listNr2jCatalog({ active: true }).catch(() => ({ items: [] })),
      ]);
      const inv = invRes?.invoice || invRes;
      setInvoice(inv);
      setCatalog(catalogRes?.items || []);
      applyInvoiceToForm(inv);
      if (inv.organizationId) {
        try {
          const detail = await apiService.getNr2jOrganization(inv.organizationId);
          setOrg(detail?.organization || detail);
        } catch {
          setOrg(inv.organization || null);
        }
      }
    } catch (error) {
      if (!silent) {
        toast.error(error?.message || 'Facture introuvable');
        navigate('/nr2j/facturation');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  async function saveDraft() {
    setSaving(true);
    try {
      const res = await apiService.patchNr2jInvoice(id, {
        contactId: contactId || null,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
        notesClient: notesClient || null,
        notesInternal: notesInternal || null,
        paymentTerms: paymentTerms || null,
        paymentInstructions: paymentInstructions || null,
        lines: lines.map(lineToPayload),
      });
      const inv = res?.invoice || res;
      setInvoice(inv);
      applyInvoiceToForm(inv);
      toast.success('Brouillon enregistré');
    } catch (error) {
      toast.error(error?.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  }

  function resumeIssueFromContinuePayload(payload) {
    return issueInvoice({
      fromContinue: true,
      org: payload?.org,
      contacts: payload?.contacts,
      skipPreflight: Boolean(payload?.org),
    });
  }

  async function issueInvoice(options = {}) {
    const run = async () => {
      setActionBusy(true);
      try {
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        const contactIdCtx = options.contactId !== undefined ? options.contactId : contactId;

        if (options.org) {
          setOrg((prev) => ({
            ...(prev || {}),
            ...options.org,
            contacts: options.contacts || prev?.contacts,
          }));
        }

        if (isDraft && !options.fromContinue) {
          await apiService.patchNr2jInvoice(id, {
            contactId: contactId || null,
            dueAt: dueAt ? new Date(dueAt).toISOString() : null,
            notesClient: notesClient || null,
            notesInternal: notesInternal || null,
            paymentTerms: paymentTerms || null,
            paymentInstructions: paymentInstructions || null,
            lines: lines.map(lineToPayload),
          });
        }

        if (!options.skipPreflight) {
          const pre = preflightInvoiceAction({
            org: orgCtx,
            contacts: contactsCtx,
            contactId: contactIdCtx,
            intent: NR2J_CONTINUE_INTENTS.INVOICE_ISSUE,
          });
          if (pre) {
            openContinue(pre, (payload) => resumeIssueFromContinuePayload(payload));
            return;
          }
        }
        const res = await apiService.issueNr2jInvoice(id);
        setInvoice(res?.invoice || res);
        toast.success('Facture émise');
        await load({ silent: true });
      } catch (error) {
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        const req = requirementsFromError(error, {
          intent: NR2J_CONTINUE_INTENTS.INVOICE_ISSUE,
          org: orgCtx,
          contacts: contactsCtx,
        });
        if (req) {
          openContinue(req, (payload) => resumeIssueFromContinuePayload(payload));
          return;
        }
        toast.error(error?.message || 'Émission impossible');
      } finally {
        setActionBusy(false);
      }
    };
    if (options.fromContinue) {
      await run();
      return;
    }
    await withActionLock(run);
  }

  async function downloadPdf() {
    setActionBusy(true);
    try {
      await apiService.downloadNr2jInvoicePdf(id, { number: invoice?.number });
    } catch (error) {
      toast.error(error?.message || 'PDF indisponible');
    } finally {
      setActionBusy(false);
    }
  }

  function resumeSendFromContinuePayload(payload) {
    return sendInvoice({
      fromContinue: true,
      org: payload?.org,
      contacts: payload?.contacts,
      skipPreflight: Boolean(payload?.org),
    });
  }

  async function sendInvoice(options = {}) {
    const run = async () => {
      setActionBusy(true);
      try {
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        const contactIdCtx = options.contactId !== undefined ? options.contactId : contactId;

        if (options.org) {
          setOrg((prev) => ({
            ...(prev || {}),
            ...options.org,
            contacts: options.contacts || prev?.contacts,
          }));
        }

        if (!options.skipPreflight) {
          const pre = preflightInvoiceAction({
            org: orgCtx,
            contacts: contactsCtx,
            contactId: contactIdCtx,
            intent: NR2J_CONTINUE_INTENTS.INVOICE_SEND,
          });
          if (pre) {
            openContinue(pre, (payload) => resumeSendFromContinuePayload(payload));
            return;
          }
        }
        const res = await apiService.sendNr2jInvoice(id);
        setInvoice(res?.invoice || res);
        toast.success('Facture envoyée');
        await load({ silent: true });
      } catch (error) {
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        const req = requirementsFromError(error, {
          intent: NR2J_CONTINUE_INTENTS.INVOICE_SEND,
          org: orgCtx,
          contacts: contactsCtx,
        });
        if (req) {
          openContinue(req, (payload) => resumeSendFromContinuePayload(payload));
          return;
        }
        toast.error(error?.message || 'Envoi impossible');
      } finally {
        setActionBusy(false);
      }
    };
    if (options.fromContinue) {
      await run();
      return;
    }
    await withActionLock(run);
  }

  async function deleteDraft() {
    if (!window.confirm('Supprimer définitivement ce brouillon de facture ?')) return;
    setActionBusy(true);
    try {
      await apiService.deleteNr2jInvoice(id);
      toast.success('Brouillon supprimé');
      navigate('/nr2j/facturation');
    } catch (error) {
      toast.error(error?.message || 'Suppression impossible');
    } finally {
      setActionBusy(false);
    }
  }

  async function submitPayment(e) {
    e.preventDefault();
    const amountCents = eurosToCents(paymentForm.amountEuros);
    if (amountCents < 1) {
      toast.error('Montant invalide');
      return;
    }
    setActionBusy(true);
    try {
      const res = await apiService.addNr2jInvoicePayment(id, {
        amountCents,
        method: paymentForm.method,
        reference: paymentForm.reference || null,
        paidAt: paymentForm.paidAt ? new Date(paymentForm.paidAt).toISOString() : null,
      });
      setInvoice(res?.invoice || res);
      setPaymentOpen(false);
      setPaymentForm({ amountEuros: '', method: 'VIREMENT', reference: '', paidAt: '' });
      toast.success('Paiement enregistré');
      await load({ silent: true });
    } catch (error) {
      toast.error(error?.message || 'Paiement impossible');
    } finally {
      setActionBusy(false);
    }
  }

  async function submitMarkPaid(e) {
    e.preventDefault();
    setActionBusy(true);
    try {
      const res = await apiService.markNr2jInvoicePaid(id, {
        method: paymentForm.method,
        reference: paymentForm.reference || null,
        paidAt: paymentForm.paidAt ? new Date(paymentForm.paidAt).toISOString() : null,
      });
      setInvoice(res?.invoice || res);
      setMarkPaidOpen(false);
      toast.success('Facture marquée payée');
      await load({ silent: true });
    } catch (error) {
      toast.error(error?.message || 'Action impossible');
    } finally {
      setActionBusy(false);
    }
  }

  async function createCreditNote() {
    if (!invoice?.lines?.length) {
      toast.error('La facture n’a pas de lignes à créditer');
      return;
    }
    if (!window.confirm('Créer un avoir brouillon sur cette facture ?')) return;
    setActionBusy(true);
    try {
      const res = await apiService.createNr2jCreditNote(id, {
        reason: null,
        lines: (invoice.lines || []).map((l, index) => ({
          invoiceLineId: l.id,
          catalogItemId: null,
          sortOrder: index,
          name: l.name,
          description: l.description,
          quantity: l.quantity,
          unitPriceHtCents: l.unitPriceHtCents,
          vatRateBps: l.vatRateBps,
          billingType: l.billingType,
          discountPercentBps: l.discountPercentBps,
          discountAmountHtCents: l.discountAmountHtCents,
        })),
      });
      const cn = res?.creditNote || res;
      toast.success('Avoir brouillon créé');
      navigate(`/nr2j/avoirs/${cn.id}`);
    } catch (error) {
      toast.error(error?.message || 'Création d’avoir impossible');
    } finally {
      setActionBusy(false);
    }
  }

  function addCatalogLine(itemId) {
    const item = catalog.find((c) => c.id === itemId);
    if (!item) return;
    setLines((prev) => [
      ...prev,
      {
        ...emptyLine(),
        catalogItemId: item.id,
        name: item.name,
        description: item.description || '',
        priceEuros: centsToEuros(item.unitPriceHtCents),
        unitPriceHtCents: item.unitPriceHtCents,
        vatRateBps: item.vatRateBps,
        billingType: item.billingType,
      },
    ]);
  }

  if (loading || !invoice) {
    return (
      <div className="p-8 text-sm text-gray-500" data-testid="nr2j-invoice-loading">
        Chargement de la facture…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nr2j-invoice-detail">
      <div
        className="sticky top-0 z-20 -mx-1 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200/80 bg-white/90 px-1 py-3 backdrop-blur"
        data-testid="nr2j-invoice-sticky-actions"
      >
        <div className="min-w-0">
          <Link to="/nr2j/facturation" className="inline-flex items-center gap-1 text-sm text-indigo-700">
            <ArrowLeft className="h-4 w-4" /> Retour à la facturation
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              {invoice.number || 'Brouillon'}
            </h1>
            <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          </div>
          <p className="text-sm text-gray-600">
            {organizationDisplayName(org || invoice.organization)}
            {org?.id || invoice.organizationId ? (
              <>
                {' · '}
                <Link
                  to={`/nr2j/clients/${org?.id || invoice.organizationId}`}
                  className="text-indigo-700 hover:underline"
                >
                  Ouvrir la fiche société
                </Link>
              </>
            ) : null}
            {invoice.sourceQuoteId ? (
              <>
                {' · '}
                <Link
                  to={`/nr2j/devis/${invoice.sourceQuoteId}`}
                  className="text-indigo-700 hover:underline"
                >
                  Devis {invoice.sourceQuoteNumber || ''}
                </Link>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDraft ? (
            <>
              <button
                type="button"
                disabled={saving || actionBusy}
                onClick={saveDraft}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium"
              >
                Enregistrer
              </button>
              <button
                type="button"
                disabled={actionBusy}
                onClick={downloadPdf}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium"
              >
                <Download className="mr-1 inline h-4 w-4" /> Aperçu PDF
              </button>
              <button
                type="button"
                disabled={actionBusy}
                onClick={issueInvoice}
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
              >
                <Send className="h-4 w-4" /> Émettre
              </button>
              <button
                type="button"
                disabled={actionBusy}
                onClick={deleteDraft}
                className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
              >
                <Trash2 className="h-4 w-4" /> Supprimer
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={actionBusy}
                onClick={downloadPdf}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <Download className="h-4 w-4" /> PDF
              </button>
              <button
                type="button"
                disabled={actionBusy}
                onClick={sendInvoice}
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
              >
                <Send className="h-4 w-4" /> Envoyer
              </button>
              {invoice.paymentState !== 'PAID' ? (
                <>
                  <button
                    type="button"
                    disabled={actionBusy}
                    onClick={() => {
                      setPaymentForm({
                        amountEuros: centsToEuros(invoice.balanceCents),
                        method: 'VIREMENT',
                        reference: '',
                        paidAt: toDateInputValue(new Date()),
                      });
                      setPaymentOpen(true);
                    }}
                    className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
                  >
                    <Banknote className="h-4 w-4" /> Ajouter un paiement
                  </button>
                  <button
                    type="button"
                    disabled={actionBusy}
                    onClick={() => {
                      setPaymentForm({
                        amountEuros: '',
                        method: 'VIREMENT',
                        reference: '',
                        paidAt: toDateInputValue(new Date()),
                      });
                      setMarkPaidOpen(true);
                    }}
                    className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-600 px-3 py-2 text-sm font-medium text-white"
                  >
                    <CheckCircle2 className="h-4 w-4" /> Marquer payée
                  </button>
                </>
              ) : null}
              {isIssuedOrSent && invoice.creditState !== 'FULL' ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={createCreditNote}
                  className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-900"
                >
                  <FileText className="h-4 w-4" /> Créer un avoir
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase text-gray-500">TTC</p>
          <p className="mt-1 text-lg font-semibold">{formatCentsAsEuros(invoice.totalTtcCents)} €</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase text-gray-500">Payé</p>
          <p className="mt-1 text-lg font-semibold">{formatCentsAsEuros(invoice.totalPaymentsCents)} €</p>
          <p className="text-xs text-gray-500">{nr2jPaymentStateLabel(invoice.paymentState)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase text-gray-500">Avoirs</p>
          <p className="mt-1 text-lg font-semibold">{formatCentsAsEuros(invoice.totalCreditsIssuedCents)} €</p>
          <p className="text-xs text-gray-500">{nr2jCreditStateLabel(invoice.creditState)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase text-gray-500">Solde</p>
          <p className="mt-1 text-lg font-semibold">{formatCentsAsEuros(invoice.balanceCents)} €</p>
          <p className="text-xs text-gray-500">
            Échéance {formatNr2jDateOnly(invoice.dueAt)}
            {invoice.overdue ? ' · En retard' : ''}
          </p>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Lignes</h2>
            {isDraft ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <select
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addCatalogLine(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">Ajouter depuis le catalogue…</option>
                  {catalog.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} — {item.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setLines((prev) => [...prev, emptyLine()])}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <Plus className="h-4 w-4" /> Ligne libre
                </button>
              </div>
            ) : null}
            <div className="mt-4 space-y-4">
              {lines.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune ligne.</p>
              ) : (
                lines.map((line, idx) => {
                  const lineTotals = computeQuoteLineTotals(lineToPayload(line, 0));
                  return (
                    <div key={line.key} className="rounded-lg border border-gray-100 p-3">
                      {isDraft ? (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <label className="block text-sm sm:col-span-2">
                            <span className="text-gray-500">Désignation *</span>
                            <input
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.name}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, name: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Qté</span>
                            <input
                              type="number"
                              min="1"
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.quantity}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, quantity: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Prix unitaire HT (€)</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.priceEuros}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, priceEuros: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">TVA</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.vatRateBps}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, vatRateBps: Number(e.target.value) } : l
                              )))}
                            >
                              {NR2J_VAT_OPTIONS.map((v) => (
                                <option key={v.bps} value={v.bps}>{v.label}</option>
                              ))}
                            </select>
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Facturation</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.billingType}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, billingType: e.target.value } : l
                              )))}
                            >
                              {NR2J_CATALOG_BILLING_TYPES.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                      ) : (
                        <div>
                          <p className="font-medium text-gray-900">{line.name}</p>
                          <p className="text-xs text-gray-500">
                            Qté {line.quantity} · {centsToEuros(line.unitPriceHtCents)} € HT
                          </p>
                        </div>
                      )}
                      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                        <span>
                          HT {formatCentsAsEuros(lineTotals.lineHtCents)} € · TTC{' '}
                          {formatCentsAsEuros(lineTotals.lineTtcCents)} €
                        </span>
                        {isDraft ? (
                          <button
                            type="button"
                            className="text-rose-700 hover:underline"
                            onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            Supprimer
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          {isDraft ? (
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <label className="block text-sm">
                <span className="text-gray-500">Contact</span>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={contactId}
                  onChange={(e) => setContactId(e.target.value)}
                >
                  <option value="">—</option>
                  {(org?.contacts || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {contactDisplayName(c) || c.id}
                      {c.email ? ` (${c.email})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Échéance</span>
                <input
                  type="date"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Conditions de paiement</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Instructions de paiement</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={paymentInstructions}
                  onChange={(e) => setPaymentInstructions(e.target.value)}
                />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Note client</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={notesClient}
                  onChange={(e) => setNotesClient(e.target.value)}
                />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Note interne</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={notesInternal}
                  onChange={(e) => setNotesInternal(e.target.value)}
                />
              </label>
            </section>
          ) : (
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Infos</h2>
              <dl className="mt-3 space-y-2">
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Émise le</dt>
                  <dd>{formatNr2jDate(invoice.issuedAt)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Envoyée le</dt>
                  <dd>{formatNr2jDate(invoice.sentAt)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Échéance</dt>
                  <dd>{formatNr2jDateOnly(invoice.dueAt)}</dd>
                </div>
                {invoice.paymentTerms ? (
                  <div>
                    <dt className="text-gray-500">Conditions</dt>
                    <dd className="mt-1 whitespace-pre-wrap text-gray-800">{invoice.paymentTerms}</dd>
                  </div>
                ) : null}
                {invoice.paymentInstructions ? (
                  <div>
                    <dt className="text-gray-500">Instructions</dt>
                    <dd className="mt-1 whitespace-pre-wrap text-gray-800">{invoice.paymentInstructions}</dd>
                  </div>
                ) : null}
                {invoice.notesClient ? (
                  <div>
                    <dt className="text-gray-500">Note client</dt>
                    <dd className="mt-1 whitespace-pre-wrap text-gray-800">{invoice.notesClient}</dd>
                  </div>
                ) : null}
              </dl>
            </section>
          )}

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="nr2j-invoice-payments">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Paiements</h2>
            {(invoice.payments || []).length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">Aucun paiement enregistré.</p>
            ) : (
              <ul className="mt-3 divide-y divide-gray-100">
                {(invoice.payments || []).map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <div>
                      <p className="font-medium text-gray-900">
                        {formatCentsAsEuros(p.amountCents)} € · {p.methodLabel || nr2jPaymentMethodLabel(p.method)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatNr2jDate(p.paidAt || p.createdAt)}
                        {p.reference ? ` · ${p.reference}` : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="nr2j-invoice-credit-notes">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Avoirs</h2>
            {(invoice.creditNotes || []).length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">Aucun avoir.</p>
            ) : (
              <ul className="mt-3 divide-y divide-gray-100">
                {(invoice.creditNotes || []).map((cn) => {
                  const cnBadge = nr2jCreditNoteStatusBadge(cn.status);
                  return (
                    <li key={cn.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                      <div>
                        <p className="font-medium text-gray-900">{cn.number || 'Brouillon'}</p>
                        <p className="text-xs text-gray-500">
                          {formatCentsAsEuros(cn.totalTtcCents)} € ·{' '}
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${cnBadge.className}`}>
                            {cnBadge.label}
                          </span>
                        </p>
                      </div>
                      <Link to={`/nr2j/avoirs/${cn.id}`} className="text-xs text-indigo-700 hover:underline">
                        Ouvrir
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Totaux</h2>
            <div className="mt-3 space-y-4 text-sm">
              {billingTotals.map((block) => (
                <div key={block.billingType}>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {block.label}
                  </p>
                  <dl className="space-y-1">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">HT{block.periodSuffix}</dt>
                      <dd className="font-medium">
                        {formatCentsAsEuros(block.totalHtCents)} €{block.periodSuffix}
                      </dd>
                    </div>
                    <div className="flex justify-between border-t border-gray-100 pt-1">
                      <dt className="font-semibold text-gray-900">TTC{block.periodSuffix}</dt>
                      <dd className="font-bold text-gray-900">
                        {formatCentsAsEuros(block.totalTtcCents)} €{block.periodSuffix}
                      </dd>
                    </div>
                  </dl>
                </div>
              ))}
              {billingTotals.length === 0 ? (
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Total HT</dt>
                    <dd className="font-medium">{formatCentsAsEuros(totals.totalHtCents)} €</dd>
                  </div>
                  <div className="flex justify-between border-t border-gray-100 pt-2">
                    <dt className="font-semibold text-gray-900">Total TTC</dt>
                    <dd className="font-bold text-gray-900">{formatCentsAsEuros(totals.totalTtcCents)} €</dd>
                  </div>
                </dl>
              ) : null}
            </div>
          </div>
        </aside>
      </div>

      {paymentOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button type="button" className="absolute inset-0 bg-black/30" aria-label="Fermer" onClick={() => setPaymentOpen(false)} />
          <form onSubmit={submitPayment} className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Ajouter un paiement</h3>
            <label className="mt-3 block text-sm">
              <span className="text-gray-500">Montant (€)</span>
              <input
                required
                type="number"
                min="0.01"
                step="0.01"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={paymentForm.amountEuros}
                onChange={(e) => setPaymentForm((f) => ({ ...f, amountEuros: e.target.value }))}
              />
            </label>
            <label className="mt-3 block text-sm">
              <span className="text-gray-500">Moyen</span>
              <select
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={paymentForm.method}
                onChange={(e) => setPaymentForm((f) => ({ ...f, method: e.target.value }))}
              >
                {NR2J_INVOICE_PAYMENT_METHODS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </label>
            <label className="mt-3 block text-sm">
              <span className="text-gray-500">Référence</span>
              <input
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm((f) => ({ ...f, reference: e.target.value }))}
              />
            </label>
            <label className="mt-3 block text-sm">
              <span className="text-gray-500">Date</span>
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={paymentForm.paidAt}
                onChange={(e) => setPaymentForm((f) => ({ ...f, paidAt: e.target.value }))}
              />
            </label>
            <div className="mt-4 flex gap-2">
              <button type="button" className="flex-1 rounded-lg border px-3 py-2 text-sm" onClick={() => setPaymentOpen(false)}>
                Annuler
              </button>
              <button type="submit" disabled={actionBusy} className="flex-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white">
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {markPaidOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button type="button" className="absolute inset-0 bg-black/30" aria-label="Fermer" onClick={() => setMarkPaidOpen(false)} />
          <form onSubmit={submitMarkPaid} className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Marquer payée</h3>
            <p className="mt-2 text-sm text-gray-600">
              Enregistre un paiement du solde restant ({formatCentsAsEuros(invoice.balanceCents)} €).
            </p>
            <label className="mt-3 block text-sm">
              <span className="text-gray-500">Moyen</span>
              <select
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={paymentForm.method}
                onChange={(e) => setPaymentForm((f) => ({ ...f, method: e.target.value }))}
              >
                {NR2J_INVOICE_PAYMENT_METHODS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </label>
            <label className="mt-3 block text-sm">
              <span className="text-gray-500">Référence</span>
              <input
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm((f) => ({ ...f, reference: e.target.value }))}
              />
            </label>
            <label className="mt-3 block text-sm">
              <span className="text-gray-500">Date</span>
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={paymentForm.paidAt}
                onChange={(e) => setPaymentForm((f) => ({ ...f, paidAt: e.target.value }))}
              />
            </label>
            <div className="mt-4 flex gap-2">
              <button type="button" className="flex-1 rounded-lg border px-3 py-2 text-sm" onClick={() => setMarkPaidOpen(false)}>
                Annuler
              </button>
              <button type="submit" disabled={actionBusy} className="flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white">
                Confirmer
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <Nr2jContinueModal
        open={Boolean(continueReq)}
        requirements={continueReq?.requirements}
        onClose={closeContinue}
        onSaved={async (payload) => {
          if (payload?.org) {
            setOrg((prev) => ({
              ...(prev || {}),
              ...payload.org,
              contacts: payload.contacts || prev?.contacts,
            }));
          }
          const retry = continueReq?.retry;
          closeContinue();
          if (retry) await runContinueRetry(retry, payload);
        }}
      />

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
